import { de, enUS, es, fr, it, ru } from 'date-fns/locale';
import React, { FC } from 'react';
import { registerLocale } from 'react-datepicker';
import { Control, FieldError, useController } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { CalendarIcon } from 'static';

import { StyledErrorMessage, StyledLabel } from '../../styled';
import { SharedStyledDateIcon } from '../styled';
import { StyledDatePicker, StyledPickerWrapper } from './styled';

// Register all locales
registerLocale('en', enUS);
registerLocale('de', de);
registerLocale('fr', fr);
registerLocale('it', it);
registerLocale('es', es);
registerLocale('ru', ru);

interface DatePickerControlledV2Props {
  name: string;
  control: Control;
  minDate?: Date;
  maxDate?: Date;
  label?: string;
  error?: FieldError;
  popperModifiers?: { [key: string]: string };
}

const DatePickerControlledV2: FC<DatePickerControlledV2Props> = ({
  name,
  control,
  label,
  minDate,
  maxDate,
  error,
  popperModifiers = {},
}) => {
  const { t, i18n } = useTranslation();

  // Get current locale, fallback to 'en' if not supported
  const currentLocale = ['en', 'de', 'fr', 'it', 'es', 'ru'].includes(
    i18n.language,
  )
    ? i18n.language
    : 'en';

  const { field } = useController({
    name,
    control,
  });

  const CalendarContainer = ({ className, children }) => {
    return (
      <div
        style={{
          height: '100%',
          padding: '16px',
          backgroundColor: '#ffffff',
          color: '#304659',
          borderRadius: '10px',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          transition: 'all',
          gap: '8px',
          boxShadow: '0px 0px 8px 2px #cfcfcf',
        }}
      >
        <div
          className={className}
          style={{
            borderRadius: '8px',
            borderColor: '#ffffff',
          }}
        >
          {children}
        </div>
      </div>
    );
  };

  return (
    <>
      <StyledPickerWrapper>
        <StyledLabel htmlFor={name}>{label}</StyledLabel>
        <StyledDatePicker
          id={name || false}
          selected={field.value}
          dateFormat="dd.MM.yyyy"
          minDate={minDate}
          maxDate={maxDate}
          error={error}
          popperPlacement="bottom-left"
          popperModifiers={popperModifiers}
          onChange={field.onChange}
          calendarContainer={CalendarContainer}
          locale={currentLocale}
          calendarStartDay={1}
        />
        <SharedStyledDateIcon src={CalendarIcon} alt="" />
      </StyledPickerWrapper>
      {error && (
        <StyledErrorMessage>
          {error.message || t(`errors.${error.type}`)}
        </StyledErrorMessage>
      )}
    </>
  );
};

export default DatePickerControlledV2;
